import React, { useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Alert,
    LinearProgress
} from '@mui/material';
import {
    CheckCircle as CorrectIcon,
    Cancel as WrongIcon,
    NavigateNext as NextIcon
} from '@mui/icons-material';
import RichTextContent from '../../common/RichTextContent';

interface MultipleChoicePlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const correctOptionIds = (question: any): string[] =>
    (question?.options || []).filter((opt: any) => opt.isCorrect).map((opt: any) => opt.id);

const MultipleChoicePlayer: React.FC<MultipleChoicePlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    const learningContent = content.learningContent || '';
    const questions = (content.questions || []).map((q: any) => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : []
    }));

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState<Map<string, string[]>>(new Map());
    const [showFeedback, setShowFeedback] = useState(false);
    const [quizCompleted, setQuizCompleted] = useState(false);

    const currentQuestion = questions[currentQuestionIndex];

    const setAnswer = (questionId: string, optionIds: string[]) => {
        const newAnswers = new Map(selectedAnswers);
        if (optionIds.length === 0) {
            newAnswers.delete(questionId);
        } else {
            newAnswers.set(questionId, optionIds);
        }
        setSelectedAnswers(newAnswers);
    };

    const toggleAnswer = (questionId: string, optionId: string) => {
        const current = selectedAnswers.get(questionId) || [];
        setAnswer(
            questionId,
            current.includes(optionId)
                ? current.filter((id) => id !== optionId)
                : [...current, optionId]
        );
    };

    const handleCheckAnswer = () => {
        setShowFeedback(true);
    };

    const handleNextQuestion = () => {
        setShowFeedback(false);

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setQuizCompleted(true);
        }
    };

    const isQuestionCorrect = (question: any) => {
        const correctIds = correctOptionIds(question);
        const selected = selectedAnswers.get(question?.id) || [];

        return correctIds.length > 0
            && selected.length === correctIds.length
            && correctIds.every((id) => selected.includes(id));
    };

    const calculateScore = () => {
        const correctCount = questions.filter(isQuestionCorrect).length;

        return questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
    };

    if (questions.length === 0) {
        return (
            <Alert severity="warning">
                Keine Fragen für dieses Quiz konfiguriert.
            </Alert>
        );
    }

    if (quizCompleted) {
        const score = calculateScore();
        const passed = score >= (lesson.minScore || 70);

        const handleComplete = async () => {
            if (onProgressUpdate) {
                await onProgressUpdate(lesson.id, {
                    score: String(score),
                    status: 'completed',
                    progressPercentage: 100,
                });
            }
            onComplete();
        };

        return (
            <Box>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom>
                        Quiz abgeschlossen!
                    </Typography>

                    <Box sx={{ my: 4 }}>
                        <Typography variant="h2" color={passed ? 'success.main' : 'error.main'}>
                            {score}%
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            {selectedAnswers.size} von {questions.length} Fragen beantwortet
                        </Typography>
                    </Box>

                    {passed ? (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Herzlichen Glückwunsch! Sie haben das Quiz bestanden.
                        </Alert>
                    ) : (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Sie benötigen mindestens {lesson.minScore || 70}% zum Bestehen.
                            Bitte versuchen Sie es erneut.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        {!passed && (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setCurrentQuestionIndex(0);
                                    setSelectedAnswers(new Map());
                                    setQuizCompleted(false);
                                    setShowFeedback(false);
                                }}
                            >
                                Erneut versuchen
                            </Button>
                        )}
                        {passed && (
                            <Button variant="contained" onClick={handleComplete}>
                                Lektion abschließen
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Box>
        );
    }

    const selectedOptionIds = currentQuestion ? (selectedAnswers.get(currentQuestion.id) || []) : [];
    const multipleCorrect = correctOptionIds(currentQuestion).length > 1;

    const optionRows = (currentQuestion.options || []).map((option: any) => {
        const isSelected = selectedOptionIds.includes(option.id);
        const wronglySelected = showFeedback && isSelected && !option.isCorrect;

        return (
            <Paper
                key={option.id}
                sx={{
                    p: 2,
                    mb: 1,
                    border: 2,
                    borderColor:
                        showFeedback && option.isCorrect
                            ? 'success.main'
                            : wronglySelected
                            ? 'error.main'
                            : isSelected
                            ? 'primary.main'
                            : 'divider',
                    bgcolor:
                        showFeedback && option.isCorrect
                            ? 'success.lighter'
                            : wronglySelected
                            ? 'error.lighter'
                            : 'background.paper'
                }}
                elevation={isSelected ? 3 : 0}
            >
                <FormControlLabel
                    value={option.id}
                    control={multipleCorrect
                        ? (
                            <Checkbox
                                checked={isSelected}
                                disabled={showFeedback}
                                onChange={() => toggleAnswer(currentQuestion.id, option.id)}
                            />
                        )
                        : <Radio disabled={showFeedback} />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{option.text}</Typography>
                            {showFeedback && option.isCorrect && (
                                <CorrectIcon color="success" titleAccess="Richtige Antwort" />
                            )}
                            {wronglySelected && <WrongIcon color="error" titleAccess="Falsch gewählt" />}
                        </Box>
                    }
                    sx={{ width: '100%' }}
                />
            </Paper>
        );
    });

    return (
        <Box>
            {learningContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <RichTextContent html={learningContent} />
                </Paper>
            )}

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Frage {currentQuestionIndex + 1} von {questions.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={((currentQuestionIndex + 1) / questions.length) * 100}
                />
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {currentQuestion.question}
                </Typography>

                {multipleCorrect && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Mehrere Antworten möglich
                    </Typography>
                )}

                {multipleCorrect ? (
                    <FormGroup>{optionRows}</FormGroup>
                ) : (
                    <RadioGroup
                        value={selectedOptionIds[0] || ''}
                        onChange={(e) => setAnswer(currentQuestion.id, [e.target.value])}
                    >
                        {optionRows}
                    </RadioGroup>
                )}

                {showFeedback && (
                    <Box sx={{ mt: 3 }}>
                        <Alert severity={isQuestionCorrect(currentQuestion) ? 'success' : 'error'}>
                            <strong>{isQuestionCorrect(currentQuestion) ? 'Richtig!' : 'Leider falsch.'}</strong>
                            {currentQuestion.explanation && (
                                <Typography
                                    component="div"
                                    data-testid="mc-explanation"
                                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                                >
                                    {currentQuestion.explanation}
                                </Typography>
                            )}
                        </Alert>
                    </Box>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                {!showFeedback ? (
                    <Button
                        variant="contained"
                        onClick={handleCheckAnswer}
                        disabled={selectedOptionIds.length === 0}
                    >
                        Antwort prüfen
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        endIcon={<NextIcon />}
                        onClick={handleNextQuestion}
                    >
                        {currentQuestionIndex < questions.length - 1 ? 'Nächste Frage' : 'Quiz beenden'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default MultipleChoicePlayer;
